import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Card, Seccion, Dato, EstadoVacio } from '@/components/Card';
import { Falta } from '@/components/Falta';
import { TextField, NumberField, MoneyField, SelectField } from '@/components/Field';
import { SelloConfianza } from './SelloConfianza';
import {
  obtenerReceta,
  editarReceta,
  desactivarReceta,
  borrarReceta,
  agregarIngrediente,
  editarIngrediente,
  quitarIngrediente,
  ingredientesDe,
  costearRecetaGuardada,
} from '@/db/repos/recipes';
import { buscarInsumos, obtenerInsumo } from '@/db/repos/ingredients';
import { unidadesDisponibles, etiquetaUnidad, formatearCantidadBase } from '@/domain/units';
import { formatearCentavos, textoACentavos } from '@/domain/money';
import { mensajeDeError } from '@/db/tx';
import type { Ingredient, RecipeItem } from '@/db/types';

type Vista =
  | { modo: 'ver' }
  | { modo: 'editarReceta' }
  | { modo: 'agregar' }
  | { modo: 'editarItem'; item: RecipeItem; nombreInsumo: string };

/**
 * Detalle de una receta: ingredientes, costos y simulador de porciones.
 *
 * Los costos NO estan guardados: se calculan al momento con el costo promedio
 * vigente de cada insumo. Por eso, al registrar una compra, este numero cambia
 * solo.
 */
export function RecipeDetailPage() {
  const { id = '' } = useParams();
  const navegar = useNavigate();

  const [vista, setVista] = useState<Vista>({ modo: 'ver' });
  const [porcionesSimuladas, setPorcionesSimuladas] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // `undefined` significa "cargando"; `null` significa "no existe". Sin esta
  // distincion una receta borrada se veria como una carga eterna.
  const receta = useLiveQuery(() => obtenerReceta(id).then((r) => r ?? null), [id]);
  const costo = useLiveQuery(
    () => costearRecetaGuardada(id, porcionesSimuladas ?? undefined).then((c) => c ?? null),
    [id, porcionesSimuladas],
  );
  const items = useLiveQuery(() => ingredientesDe(id), [id]);

  if (receta === undefined || costo === undefined) {
    return <p className="text-sm text-texto-suave">Cargando…</p>;
  }

  if (receta === null || costo === null) {
    return (
      <div className="space-y-5">
        <PageHeader titulo="Receta no encontrada" />
        <Link to="/recetas">
          <Button variante="secundario">Volver a recetas</Button>
        </Link>
      </div>
    );
  }

  if (vista.modo === 'editarReceta') {
    return (
      <EditarRecetaForm
        recipeId={id}
        nombreInicial={receta.nombre}
        porcionesInicial={String(receta.rendimientoPorciones)}
        precioInicial={
          receta.precioVentaCentavos > 0 ? (receta.precioVentaCentavos / 100).toFixed(2) : ''
        }
        diasInicial={String(receta.diasAnticipacion)}
        instruccionesInicial={receta.instrucciones ?? ''}
        onListo={() => setVista({ modo: 'ver' })}
        onCancelar={() => setVista({ modo: 'ver' })}
      />
    );
  }

  if (vista.modo === 'agregar') {
    return (
      <AgregarIngredienteForm
        recipeId={id}
        yaUsados={costo.renglones.map((r) => r.ingredientId)}
        onListo={() => setVista({ modo: 'ver' })}
        onCancelar={() => setVista({ modo: 'ver' })}
      />
    );
  }

  if (vista.modo === 'editarItem') {
    return (
      <EditarIngredienteForm
        item={vista.item}
        nombreInsumo={vista.nombreInsumo}
        onListo={() => setVista({ modo: 'ver' })}
        onCancelar={() => setVista({ modo: 'ver' })}
      />
    );
  }

  const simulando = porcionesSimuladas !== null && porcionesSimuladas !== receta.rendimientoPorciones;

  async function desactivar() {
    setError(null);
    try {
      await desactivarReceta(id);
      void navegar('/recetas');
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  async function borrar() {
    setError(null);
    try {
      await borrarReceta(id);
      void navegar('/recetas');
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        titulo={receta.nombre}
        descripcion={`Rinde ${receta.rendimientoPorciones} ${
          receta.rendimientoPorciones === 1 ? 'porción' : 'porciones'
        } · se prepara ${
          receta.diasAnticipacion === 0
            ? 'el mismo día'
            : `${receta.diasAnticipacion} ${receta.diasAnticipacion === 1 ? 'día' : 'días'} antes`
        }`}
      />

      {/* --- Panel de costos ------------------------------------------- */}
      <Card className={simulando ? 'border-marca bg-marca-suave' : 'bg-superficie-suave'}>
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-sm text-texto-suave">
            {simulando ? `Costo de ${costo.porciones} porciones` : 'Costo de la receta completa'}
          </p>
          <SelloConfianza confianza={costo.confianza} />
        </div>

        <p className="mt-1 text-3xl font-bold tabular-nums text-texto">
          {formatearCentavos(costo.costoTotalCentavos)}
        </p>

        <dl className="mt-3 divide-y divide-borde">
          <Dato
            etiqueta="Costo por porción"
            valor={formatearCentavos(costo.costoPorPorcionCentavos)}
          />
          {receta.precioVentaCentavos > 0 && (
            <>
              <Dato
                etiqueta="Precio de venta"
                valor={formatearCentavos(receta.precioVentaCentavos)}
              />
              <Dato
                etiqueta="Ganancia por porción"
                valor={
                  costo.gananciaPorPorcionCentavos === null ? (
                    <span className="text-texto-tenue">no disponible</span>
                  ) : (
                    formatearCentavos(costo.gananciaPorPorcionCentavos)
                  )
                }
              />
              <Dato
                etiqueta="Margen"
                valor={
                  costo.margenPorcentaje === null ? (
                    <span className="text-texto-tenue">no disponible</span>
                  ) : (
                    `${costo.margenPorcentaje.toFixed(1)} %`
                  )
                }
              />
            </>
          )}
        </dl>
      </Card>

      {receta.precioVentaCentavos === 0 && (
        <Banner tono="info" titulo="Sin precio de venta">
          Pon el precio al que vendes cada porción y la app te va a decir cuánto ganas y con qué
          margen.
        </Banner>
      )}

      {costo.confianza === 'incompleto' && (
        <Banner tono="alerta" titulo="Falta el costo de algún insumo">
          El total de arriba está <strong>incompleto</strong>: solo suma lo que sí se pudo costear.
          Falta el precio de {listaLegible(costo.nombresSinCosto)}. Registra una compra de{' '}
          {costo.nombresSinCosto.length === 1 ? 'ese insumo' : 'esos insumos'} y el costo queda
          completo.
        </Banner>
      )}

      {costo.confianza === 'estimado' && (
        <Banner tono="aviso" titulo="Costo estimado">
          Algún insumo se costeó con el precio de su última compra y no con un promedio de tus
          compras. La cifra sirve, pero puede moverse.
        </Banner>
      )}

      {/* --- Simulador de porciones ------------------------------------ */}
      <Seccion
        titulo="Simulador de porciones"
        descripcion="Cuánto necesitas y cuánto cuesta si cambias la cantidad"
      >
        <div className="flex flex-wrap gap-2">
          {porcionesSugeridas(receta.rendimientoPorciones).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setPorcionesSimuladas(n === receta.rendimientoPorciones ? null : n)}
              aria-pressed={costo.porciones === n}
              className={`min-h-11 min-w-14 rounded-xl border px-3 text-base font-medium tabular-nums ${
                costo.porciones === n
                  ? 'border-marca bg-marca text-white'
                  : 'border-borde-fuerte bg-superficie text-texto active:bg-superficie-suave'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        {simulando && (
          <Button variante="fantasma" onClick={() => setPorcionesSimuladas(null)}>
            Volver a {receta.rendimientoPorciones} porciones
          </Button>
        )}
      </Seccion>

      {/* --- Ingredientes --------------------------------------------- */}
      <Seccion
        titulo="Ingredientes"
        descripcion={
          simulando
            ? `Cantidades escaladas a ${costo.porciones} porciones`
            : 'Cantidades para la receta completa'
        }
      >
        {costo.renglones.length === 0 ? (
          <EstadoVacio
            titulo="Todavía no hay ingredientes"
            descripcion="Agrega los insumos que usas y sus cantidades. El costo se calcula solo."
          />
        ) : (
          <ul className="space-y-2">
            {costo.renglones.map((renglon) => (
              <li key={renglon.itemId}>
                <Card>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-texto">{renglon.nombreInsumo}</p>
                      <p className="text-sm text-texto-suave">
                        {renglon.cantidadEscalada}
                        {simulando && (
                          <span className="text-texto-tenue">
                            {' '}
                            (receta: {renglon.cantidadCaptura})
                          </span>
                        )}
                      </p>
                      <p className="mt-0.5 text-xs text-texto-tenue">
                        {renglon.sinCosto ? 'sin costo registrado' : `$${renglon.costoUnitarioTexto}`}
                      </p>
                      {renglon.notas !== null && (
                        <p className="mt-1 text-xs italic text-texto-suave">{renglon.notas}</p>
                      )}
                    </div>

                    <div className="shrink-0 text-right">
                      {renglon.costoCentavos === null ? (
                        <span className="text-sm font-medium text-alerta">falta precio</span>
                      ) : (
                        <span className="text-base font-semibold tabular-nums text-texto">
                          {formatearCentavos(renglon.costoCentavos)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex gap-2">
                    <Button
                      variante="fantasma"
                      completo={false}
                      disabled={items === undefined}
                      onClick={() => {
                        const item = items?.find((i) => i.id === renglon.itemId);
                        if (item !== undefined) {
                          setVista({
                            modo: 'editarItem',
                            item,
                            nombreInsumo: renglon.nombreInsumo,
                          });
                        }
                      }}
                    >
                      Cambiar cantidad
                    </Button>
                    <Button
                      variante="fantasma"
                      completo={false}
                      onClick={() => void quitarIngrediente(renglon.itemId)}
                    >
                      Quitar
                    </Button>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}

        <Button tamano="grande" onClick={() => setVista({ modo: 'agregar' })}>
          + Agregar ingrediente
        </Button>
      </Seccion>

      {receta.instrucciones !== null && receta.instrucciones !== '' && (
        <Seccion titulo="Instrucciones">
          <Card>
            <p className="whitespace-pre-wrap text-sm text-texto">{receta.instrucciones}</p>
          </Card>
        </Seccion>
      )}

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo completar">
          {error}
        </Banner>
      )}

      <Seccion titulo="Esta receta">
        <div className="space-y-2">
          <Button variante="secundario" onClick={() => setVista({ modo: 'editarReceta' })}>
            Editar nombre, rendimiento y precio
          </Button>
          <Button variante="secundario" onClick={() => void desactivar()}>
            Archivar receta
          </Button>
          <Button variante="fantasma" onClick={() => void borrar()}>
            Borrar definitivamente
          </Button>
          <p className="text-xs text-texto-tenue">
            Archivar la saca de la lista pero conserva su historial. Borrar solo se puede si la
            receta nunca se usó en un menú ni en una venta.
          </p>
        </div>
      </Seccion>
    </div>
  );
}

/** Opciones del simulador: el rendimiento y valores alrededor, sin repetir. */
function porcionesSugeridas(rendimiento: number): number[] {
  const candidatos = [
    Math.round(rendimiento / 2),
    rendimiento,
    Math.round(rendimiento * 1.5),
    rendimiento * 2,
  ];

  return [...new Set(candidatos.filter((n) => n >= 1))].sort((a, b) => a - b);
}

function listaLegible(nombres: readonly string[]): string {
  if (nombres.length === 0) return '';
  if (nombres.length === 1) return nombres[0] ?? '';
  return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
}

// ---------------------------------------------------------------------------

interface PropsEditar {
  recipeId: string;
  nombreInicial: string;
  porcionesInicial: string;
  precioInicial: string;
  diasInicial: string;
  instruccionesInicial: string;
  onListo: () => void;
  onCancelar: () => void;
}

function EditarRecetaForm({
  recipeId,
  nombreInicial,
  porcionesInicial,
  precioInicial,
  diasInicial,
  instruccionesInicial,
  onListo,
  onCancelar,
}: PropsEditar) {
  const [nombre, setNombre] = useState(nombreInicial);
  const [porciones, setPorciones] = useState(porcionesInicial);
  const [precio, setPrecio] = useState(precioInicial);
  const [dias, setDias] = useState(diasInicial);
  const [instrucciones, setInstrucciones] = useState(instruccionesInicial);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const porcionesNumero = Number(porciones);
  const diasNumero = Number(dias);

  const faltan: string[] = [];
  if (nombre.trim() === '') faltan.push('el nombre');
  if (!Number.isFinite(porcionesNumero) || porcionesNumero <= 0) {
    faltan.push('cuántas porciones rinde');
  }
  const valida = faltan.length === 0;

  async function guardar() {
    setGuardando(true);
    setError(null);

    try {
      await editarReceta(recipeId, {
        nombre,
        rendimientoPorciones: porcionesNumero,
        precioVentaCentavos: textoACentavos(precio) ?? 0,
        ...(Number.isFinite(diasNumero) ? { diasAnticipacion: diasNumero } : {}),
        instrucciones: instrucciones.trim() === '' ? null : instrucciones,
      });
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader titulo="Editar receta" />

      <TextField etiqueta="Nombre del platillo" valor={nombre} onCambio={setNombre} maxLength={80} />

      <NumberField
        etiqueta="Cuántas porciones rinde"
        valor={porciones}
        onCambio={setPorciones}
        ayuda="Cambiarlo modifica el costo por porción, no las cantidades de los ingredientes"
        decimal={false}
      />

      <MoneyField
        etiqueta="Precio de venta por porción"
        valor={precio}
        onCambio={setPrecio}
        ayuda="Déjalo vacío si todavía no lo decides"
      />

      <NumberField
        etiqueta="Días de anticipación"
        valor={dias}
        onCambio={setDias}
        ayuda="Cero si se vende el mismo día que se cocina"
        decimal={false}
      />

      <div className="space-y-1">
        <label htmlFor="instrucciones" className="block text-sm font-medium text-texto">
          Instrucciones
        </label>
        <textarea
          id="instrucciones"
          value={instrucciones}
          onChange={(e) => setInstrucciones(e.target.value)}
          rows={6}
          maxLength={4000}
          className="w-full rounded-xl border border-borde-fuerte bg-superficie px-3 py-2.5 text-base text-texto"
          placeholder="Opcional. Los pasos, temperaturas, tiempos…"
        />
      </div>

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo guardar">
          {error}
        </Banner>
      )}

      <div className="space-y-2">
        <Button tamano="grande" disabled={guardando || !valida} onClick={() => void guardar()}>
          {guardando ? 'Guardando…' : 'Guardar cambios'}
        </Button>
        <Falta faltan={faltan} />
        <Button variante="secundario" disabled={guardando} onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsAgregar {
  recipeId: string;
  yaUsados: readonly string[];
  onListo: () => void;
  onCancelar: () => void;
}

/** Elige un insumo existente y dice cuánto se usa de él. */
function AgregarIngredienteForm({ recipeId, yaUsados, onListo, onCancelar }: PropsAgregar) {
  const [busqueda, setBusqueda] = useState('');
  const [encontrados, setEncontrados] = useState<Ingredient[]>([]);
  const [elegido, setElegido] = useState<Ingredient | null>(null);
  const [cantidad, setCantidad] = useState('');
  const [unidad, setUnidad] = useState('');
  const [notas, setNotas] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vigente = true;
    void buscarInsumos(busqueda).then((lista) => {
      if (vigente) setEncontrados(lista.filter((i) => !yaUsados.includes(i.id)).slice(0, 8));
    });
    return () => {
      vigente = false;
    };
  }, [busqueda, yaUsados]);

  const cantidadNumero = Number(cantidad);
  const listo = elegido !== null && Number.isFinite(cantidadNumero) && cantidadNumero > 0;

  const opcionesUnidad =
    elegido === null
      ? []
      : unidadesDisponibles({
          dimension: elegido.dimension,
          factorEquivalencia: elegido.factorEquivalencia,
        }).map((u) => ({ valor: u, etiqueta: etiquetaUnidad(u, 2) }));

  async function guardar() {
    if (elegido === null) return;
    setGuardando(true);
    setError(null);

    try {
      await agregarIngrediente({
        recipeId,
        ingredientId: elegido.id,
        cantidadCaptura: cantidadNumero,
        unidadCaptura: unidad,
        notas: notas.trim() === '' ? null : notas,
      });
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader titulo="Agregar ingrediente" descripcion="De tus insumos ya registrados" />

      {elegido === null ? (
        <>
          <TextField
            etiqueta="Insumo"
            valor={busqueda}
            onCambio={setBusqueda}
            placeholder="Escribe para buscar…"
            autoFocus
          />

          {encontrados.length > 0 && (
            <ul className="space-y-1.5">
              {encontrados.map((insumo) => (
                <li key={insumo.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setElegido(insumo);
                      setUnidad(insumo.unidadCompraPreferida);
                      setBusqueda('');
                    }}
                    className="flex min-h-11 w-full items-center justify-between gap-3 rounded-xl border border-borde bg-superficie px-3 py-2.5 text-left active:bg-superficie-suave"
                  >
                    <span className="font-medium text-texto">{insumo.nombre}</span>
                    <span className="text-sm text-texto-suave">
                      {formatearCantidadBase(
                        insumo.stockBase,
                        insumo.dimension,
                        insumo.unidadCompraPreferida,
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {encontrados.length === 0 && (
            <EstadoVacio
              titulo={
                busqueda.trim() === ''
                  ? 'No hay insumos disponibles'
                  : `Ningún insumo coincide con «${busqueda.trim()}»`
              }
              descripcion="Los insumos se crean desde la pantalla de Insumos o al registrar una compra."
            >
              <Link to="/insumos">
                <Button variante="secundario">Ir a Insumos</Button>
              </Link>
            </EstadoVacio>
          )}
        </>
      ) : (
        <>
          <Card>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-texto-tenue">Insumo</p>
                <p className="font-semibold text-texto">{elegido.nombre}</p>
              </div>
              <Button variante="fantasma" completo={false} onClick={() => setElegido(null)}>
                Cambiar
              </Button>
            </div>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <NumberField
              etiqueta="Cuánto se usa"
              valor={cantidad}
              onCambio={setCantidad}
              placeholder="500"
              autoFocus
            />
            <SelectField
              etiqueta="Unidad"
              valor={unidad}
              opciones={opcionesUnidad}
              onCambio={setUnidad}
            />
          </div>

          <TextField
            etiqueta="Nota"
            valor={notas}
            onCambio={setNotas}
            placeholder="Opcional: picado, sin semillas…"
            maxLength={200}
          />

          {error !== null && (
            <Banner tono="alerta" titulo="No se pudo agregar">
              {error}
            </Banner>
          )}

          <div className="space-y-2">
            <Button tamano="grande" disabled={guardando || !listo} onClick={() => void guardar()}>
              {guardando ? 'Guardando…' : 'Agregar a la receta'}
            </Button>
            <Button variante="secundario" disabled={guardando} onClick={onCancelar}>
              Cancelar
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsEditarItem {
  item: RecipeItem;
  nombreInsumo: string;
  onListo: () => void;
  onCancelar: () => void;
}

/**
 * Cambia la cantidad de un ingrediente.
 *
 * Recibe el renglon guardado, no el texto legible: leer "8 barras" y quedarse
 * con "barras" daria una unidad que el catalogo no conoce, porque ahi la unidad
 * se llama "barra".
 */
function EditarIngredienteForm({ item, nombreInsumo, onListo, onCancelar }: PropsEditarItem) {
  const insumo = useLiveQuery(
    () => obtenerInsumo(item.ingredientId).then((i) => i ?? null),
    [item.ingredientId],
  );

  const [cantidad, setCantidad] = useState(String(item.cantidadCaptura));
  const [unidad, setUnidad] = useState(item.unidadCaptura);
  const [notas, setNotas] = useState(item.notas ?? '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cantidadNumero = Number(cantidad);

  const faltanIngrediente: string[] = [];
  if (!Number.isFinite(cantidadNumero) || cantidadNumero <= 0) {
    faltanIngrediente.push('cuánto se usa');
  }
  if (unidad === '') faltanIngrediente.push('la unidad');
  const valida = faltanIngrediente.length === 0;

  const opcionesUnidad =
    insumo === undefined || insumo === null
      ? [{ valor: item.unidadCaptura, etiqueta: etiquetaUnidad(item.unidadCaptura, 2) }]
      : unidadesDisponibles({
          dimension: insumo.dimension,
          factorEquivalencia: insumo.factorEquivalencia,
        }).map((u) => ({ valor: u, etiqueta: etiquetaUnidad(u, 2) }));

  async function guardar() {
    setGuardando(true);
    setError(null);

    try {
      await editarIngrediente(item.id, {
        cantidadCaptura: cantidadNumero,
        unidadCaptura: unidad,
        notas: notas.trim() === '' ? null : notas,
      });
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader titulo={nombreInsumo} descripcion="Cuánto se usa en la receta completa" />

      <div className="grid grid-cols-2 gap-3">
        <NumberField etiqueta="Cantidad" valor={cantidad} onCambio={setCantidad} autoFocus />
        <SelectField
          etiqueta="Unidad"
          valor={unidad}
          opciones={opcionesUnidad}
          onCambio={setUnidad}
        />
      </div>

      <TextField
        etiqueta="Nota"
        valor={notas}
        onCambio={setNotas}
        placeholder="Opcional: picado, sin semillas…"
        maxLength={200}
      />

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo guardar">
          {error}
        </Banner>
      )}

      <div className="space-y-2">
        <Button tamano="grande" disabled={guardando || !valida} onClick={() => void guardar()}>
          {guardando ? 'Guardando…' : 'Guardar cantidad'}
        </Button>
        <Falta faltan={faltanIngrediente} />
        <Button variante="secundario" disabled={guardando} onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
